/**
 * Copyright (c) 2024, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
 *
 * WSO2 LLC. licenses this file to you under the Apache License,
 * Version 2.0 (the "License"); you may not use this file except
 * in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied. See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import PropTypes from "prop-types";
import React, { createContext, useEffect, useMemo, useState } from "react";

// Create a new React context for managing cloud state
export const CloudContext = createContext();

// Define a provider component for the CloudContext
export const CloudContextProvider = ({ children }) => {
  const [ cloud, setCloud ] = useState(getInitialCloud());

  function getInitialCloud() {
    const hostname = window.location.hostname;

    return window.ENV.hostnameMapping[hostname];
  }

  useEffect(() => {
    // Update the document title and meta description based on the current cloud
    document.title = window.ENV[cloud].title;
    document.querySelector("meta[name=\"description\"]").setAttribute("content", window.ENV[cloud].description);

    // Update the Google site verification meta tag based on the current cloud
    if (window.ENV[cloud].googleSiteVerification)
      document.querySelector("meta[name=\"google-site-verification\"]")
        .setAttribute("content", window.ENV[cloud].googleSiteVerification);

    // Update the favicon based on the current cloud
    document.querySelector("link[id=\"favicon\"]").setAttribute("href", `/favicon-${cloud}.ico`);
  });

  const value = useMemo(() => ({ cloud, setCloud }), [ cloud, setCloud ]);

  return (
    <CloudContext.Provider value={ value }>{ children }</CloudContext.Provider>
  );
};

CloudContextProvider.propTypes = {
  children: PropTypes.any
};

export default CloudContext;

// Custom hook to conveniently access the cloud context values
export const useCloud = () => {
  const context = React.useContext(CloudContext);

  // Throw an error if the hook is used outside the CloudContextProvider
  if (context === undefined) {
    throw new Error("useCloud must be used within a CloudContextProvider");
  }

  // Destructure the context values for easy access
  const { cloud, setCloud } = context;

  return { cloud, setCloud };
};
